<script setup lang="ts">
import { ref } from 'vue';

import { partnersNavigation, ringRangesNavigation, ourMusesSectionGridImages } from '@/constants';
import { useImageLoaded } from '@/hooks';

const LCPImagesLoaded = ref<{ [key: string]: boolean }>({
  heroBanner: false,
  philosophyBanner: false,
  brokenStonesBanner: false,
  historyBanner: false
});
const { onImageLoaded } = useImageLoaded('homePage', LCPImagesLoaded);
</script>

<template>
  <section class="hero-section">
    <Picture
      class="absolute h-full w-full z-[-1]"
      image-classes="h-full w-full"
      image-directory="napkin-rings"
      extension="jpg"
      :alt-text="$t('images.altText.intertwinedHands')"
      width="800"
      height="800"
      :lazy-load="false"
      @image-loaded="onImageLoaded('heroBanner')"
    />
    <div class="h-full flex flex-col-center pt-navbar-h">
      <div class="flex-col-center gap-2 text-black pt-4 mt-auto">
        <Icon icon-name="BrandLogo" class="mb-2 h-8 md:h-12 lg:h-16 xl:h-20" />
        <h1>
          <Icon icon-name="BrandTitle" class="h-8 md:h-12 lg:h-16 xl:h-24 xl:w-96" />
        </h1>
      </div>
      <ScrollDown class="text-black mt-auto" />
    </div>
  </section>

  <section class="description-section">
    <i18n-t
      keypath="homePage.description.content"
      tag="div"
      class="max-w-screen-xl p-8 font-light text-beige text-3xl md:text-4xl xl:text-5xl !leading-normal"
      scope="global"
    >
      <p>{{ $t('homePage.description.content0') }}</p>
      <p>{{ $t('homePage.description.content1') }}</p>
    </i18n-t>
    <Icon icon-name="BrandLogo" class="absolute text-sand opacity-10 h-full md:w-1/3" />
  </section>

  <section class="philosophy-section">
    <Picture
      class="absolute h-full w-full z-[-1]"
      image-classes="h-full w-full"
      image-directory="interwined-hands"
      extension="jpg"
      :alt-text="$t('images.altText.napkinRings')"
      :width="2600"
      :height="1580"
      :lazy-load="false"
      @image-loaded="onImageLoaded('philosophyBanner')"
    />
    <div class="content-container">
      <div class="flex flex-col gap-4">
        <h2 class="font-brand-headings-title">
          {{ $t('homePage.philosophy.title') }}
        </h2>
        <p class="font-primary font-light whitespace-break-spaces">
          {{ $t('homePage.philosophy.content1') }}
        </p>
        <p class="font-primary font-light whitespace-break-spaces">
          {{ $t('homePage.philosophy.content2') }}
        </p>
        <p class="font-primary font-light whitespace-break-spaces">
          {{ $t('homePage.philosophy.content3') }}
        </p>
        <p class="font-primary font-light whitespace-break-spaces">
          {{ $t('homePage.philosophy.content4') }}
        </p>
      </div>
    </div>
  </section>

  <section class="ranges-section">
    <Icon icon-name="BrandUnstructured" class="brand-unstructured-ranges" />
    <h2 class="font-headings-title text-black mb-8">
      {{ $t('homePage.ranges.title') }}
    </h2>
    <div class="ranges-container">
      <div
        v-for="range in ringRangesNavigation"
        :key="range.title"
        class="flex-col-center gap-8 md:gap-16 md:w-1/3 md:max-w-xs xl:max-w-sm"
      >
        <RouterLink :to="range.path" :aria-label="range.ariaLabel">
          <Picture
            :image-classes="`h-full w-full max-md:max-w-xs ${range.classes}`"
            :image-directory="range.imageDirectoryPath"
            :extension="range.extension"
            :alt-text="$t(range.altText)"
            :width="500"
            :height="500"
          />
        </RouterLink>
        <div class="max-w-sm">
          <RouterLink :to="range.path" :aria-label="range.ariaLabel">
            <h3 class="font-brand-headings font-medium text-3xl mb-2">{{ $t(range.title) }}</h3>
          </RouterLink>
          <p class="font-primary font-light opacity-[0.65] whitespace-break-spaces">
            {{ $t(range.content) }}
          </p>
        </div>
        <RouterLink
          :to="range.path"
          class="underline-animation font-headings uppercase text-xs"
          :aria-label="range.ariaLabel"
        >
          {{ $t(`${range.cta}`) }}
        </RouterLink>
      </div>
    </div>
  </section>

  <section class="broken-stones-section">
    <Picture
      class="absolute h-full w-full z-[-1] rotate-y-180"
      image-classes="h-full w-full"
      image-directory="square-stone-gray"
      extension="jpg"
      :alt-text="$t('images.altText.stoneSilver')"
      :width="2600"
      :height="1733"
      :lazy-load="false"
      @image-loaded="onImageLoaded('brokenStonesBanner')"
    />
    <div class="content-container">
      <div class="flex flex-col gap-4 text-white max-w-lg">
        <h2 class="font-brand-headings-title">
          {{ $t('homePage.brokenStones.title') }}
        </h2>

        <i18n-t
          keypath="homePage.brokenStones.content"
          tag="div"
          class="flex flex-col gap-2 font-primary font-light leading-7"
          scope="global"
        >
          <p>{{ $t('homePage.brokenStones.content0') }}</p>
          <p>{{ $t('homePage.brokenStones.content1') }}</p>
        </i18n-t>
      </div>
    </div>
  </section>

  <section class="our-muses-section">
    <Icon icon-name="BrandUnstructured" class="brand-unstructured-our-stones-top" />
    <h2 class="font-headings-title text-black mb-8 md:mb-20">
      {{ $t('homePage.ourMuses.title') }}
    </h2>
    <DiagonalMosaic
      class="w-full z-[1] aspect-square mb-8 md:mb-60"
      :images="ourMusesSectionGridImages"
    />
    <!-- TODO uncomment and remove mb to DiagonalMosaic when page will be available -->
    <!-- <button class="button mt-8 mb-20 md:mt-16 md:mb-80">{{ $t('homePage.ourMuses.cta') }}</button> -->
    <p class="font-headings-title text-black mb-8 md:mb-20">{{ $t('navigation.history.title') }}</p>
    <Icon icon-name="BrandUnstructured" class="brand-unstructured-our-stones-bottom" />
  </section>

  <section id="history" class="history-section">
    <Picture
      class="absolute h-full w-full z-[-1]"
      image-classes="h-full w-full"
      image-directory="sketched-losange-ring"
      extension="jpg"
      :alt-text="$t('images.altText.ring')"
      :width="2600"
      :height="1580"
      :lazy-load="false"
      @image-loaded="onImageLoaded('historyBanner')"
    />
    <div class="content-container">
      <div class="flex flex-col gap-4 ml-auto max-w-lg">
        <h2 class="font-brand-headings-title">
          {{ $t('homePage.history.title') }}
        </h2>
        <i18n-t
          keypath="homePage.history.content"
          tag="div"
          class="flex flex-col gap-2 font-primary font-light leading-7"
          scope="global"
        >
          <p>{{ $t('homePage.history.content0') }}</p>
          <p class="whitespace-break-spaces">{{ $t('homePage.history.content1') }}</p>
        </i18n-t>
      </div>
    </div>
  </section>

  <section class="partners-section">
    <h2 class="font-headings-title text-white mb-16 md:mb-20">
      {{ $t('homePage.partners.title') }}
    </h2>
    <div class="partners-container">
      <div
        v-for="partner in partnersNavigation"
        :key="`${partner.i18nKey}.title`"
        class="flex flex-col items-center self-center gap-8 md:self-start"
      >
        <div class="md:w-[20rem]">
          <p class="font-brand-headings font-medium text-3xl mb-2">
            {{ $t(`${partner.i18nKey}.title`) }}
          </p>
          <i18n-t
            :keypath="`${partner.i18nKey}.content`"
            tag="div"
            class="font-primary font-light opacity-[0.65]"
            scope="global"
          >
            <template v-for="content in partner.contents" :key="content.i18nKey">
              <a
                v-if="content.link"
                :href="content.link"
                class="underline-animation whitespace-nowrap"
                rel="noreferrer"
                target="_blank"
              >
                {{ $t(`${content.i18nKey}`) }}
              </a>
              <span v-else>
                {{ $t(`${content.i18nKey}`) }}
              </span>
            </template>
          </i18n-t>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.hero-section {
  @apply h-[100dvh] relative;
}

.description-section {
  @apply min-h-screen bg-black flex-center-center relative text-center font-brand-headings;
}

.philosophy-section {
  @apply min-h-screen relative flex items-end text-white;
  background: linear-gradient(to top right, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0));
}

.ranges-section {
  @apply min-h-screen relative text-center overflow-hidden flex-col-center bg-white text-black px-12 py-20 md:py-60;
}

.partners-section {
  @apply min-h-screen relative text-center overflow-hidden flex-col-center bg-black text-white px-4 py-16;
}

.ranges-container,
.partners-container {
  @apply flex-col-center gap-16 relative md:flex-row;
}

.ranges-container {
  @apply sm:gap-8 lg:gap-16;
  @screen md {
    > div:first-child {
      @apply order-2;
    }
    > div:nth-child(2) {
      @apply order-1 mt-32;
    }
    > div:last-child {
      @apply order-3 mt-32;
    }
  }
}

.partners-container {
  @apply h-full max-w-screen-xl md:flex-wrap;
}

.broken-stones-section {
  @apply min-h-screen relative flex items-end;

  @media (max-width: theme('screens.lg')) {
    background: linear-gradient(to top right, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0));
  }
}

.our-muses-section {
  @apply relative flex-col-center overflow-hidden pt-8 md:pt-60;
}

.history-section {
  @apply min-h-screen relative;

  background: linear-gradient(to bottom left, rgba(250, 250, 250, 0.6) 40%, rgba(250, 250, 250, 0));

  @media (max-width: theme('screens.md')) {
    background: linear-gradient(to bottom, rgba(250, 250, 250, 0.6) 75%, rgba(250, 250, 250, 0));
  }
}

.content-container {
  @apply container mx-auto leading-7 px-4 py-12 md:py-24 2xl:py-32 2xl:text-xl;
}

.brand-unstructured-ranges {
  @apply absolute text-sand scale-[2] top-24 opacity-30 md:h-[38rem] md:w-[38rem] md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2;
}

.brand-unstructured-our-stones-top {
  @apply absolute z-0 text-sand scale-[2] top-24 opacity-30 md:h-[38rem] md:w-[38rem];
}

.brand-unstructured-our-stones-bottom {
  @apply absolute z-0 text-sand scale-[2] -bottom-[28rem] opacity-30 md:h-[38rem] md:w-[38rem];
}

.brand-logo {
  @apply absolute rotate-180 text-sand opacity-10 w-full h-48 -bottom-16 md:h-[32rem] md:-bottom-64;
}
</style>
